import React from "react";

import type { LucideIcon } from "lucide-react";
import {
  Activity,
  AlertTriangle,
  ArrowLeftRight,
  Calendar,
  CalendarDays,
  Camera,
  CheckCircle2,
  Clipboard,
  Clock,
  Database,
  Download,
  ExternalLink,
  Eye,
  Facebook,
  FileText,
  Gauge,
  Heart,
  Hourglass,
  Instagram,
  Key,
  Lightbulb,
  Linkedin,
  Loader2,
  Lock,
  LogOut,
  Mail,
  Pin,
  RefreshCw,
  Settings,
  Share2,
  SquarePen,
  Star,
  Trash2,
  TrendingDown,
  TrendingUp,
  Unlock,
  Upload,
  User,
  Users,
  X,
} from "lucide-react";

const ICON_MAP = {
  circleArrowHorizontalRound: ArrowLeftRight,
  database: Database,
  delegations: Share2,
  escalations: TrendingUp,
  opportunities: Lightbulb,
  person: User,
  risks: AlertTriangle,
  sentiments: Heart,
  activityPrimary: Activity,
  trendSuccess: TrendingUp,
  trendDown: TrendingDown,
  pinPrimary: Pin,
  newTabWhite: ExternalLink,
  facebook: Facebook,
  linkedin: Linkedin,
  instagram: Instagram,
  x: X,
  pencilPrimary: SquarePen,
  trash: Trash2,
  view: Eye,
  viewCalendar: CalendarDays,
  upload: Upload,
  calendarIcon: Calendar,
  completeSuccess: CheckCircle2,
  processing: Loader2,
  camera: Camera,
  starYellow: Star,
  opsWhite: Gauge,
  mail: Mail,
  pendingAssessment: Hourglass,
  ongoingAssessment: Clock,
  clock: Clock,
  completedAssessment: CheckCircle2,
  checkCircle: CheckCircle2,
  publishedAssessment: Clipboard,
  clipboard: Clipboard,
  logout: LogOut,
  settings: Settings,
  refresh: RefreshCw,
  key: Key,
  lock: Lock,
  unlock: Unlock,
  primaryDocument: FileText,
  downloadPrimary: Download,
  users: Users,
} as const satisfies Record<string, LucideIcon>;

export type IconName = keyof typeof ICON_MAP;

type IconProps = {
  name: IconName;
  size?: number | string;
  width?: number | string;
  height?: number | string;
  scale?: number;
  color?: string;
  className?: string;
  style?: React.CSSProperties;
} & Omit<React.SVGProps<SVGSVGElement>, "ref">;

function resolveSize(
  props: Pick<IconProps, "size" | "width" | "height" | "style">
): number | string | undefined {
  if (props.size != null) {
    return props.size;
  }
  if (typeof props.width === "number") {
    return props.width;
  }
  if (typeof props.height === "number") {
    return props.height;
  }
  if (typeof props.style?.width === "number") {
    return props.style.width;
  }
  return undefined;
}

function extraClassForName(name: IconName): string {
  switch (name) {
    case "processing":
      return "animate-spin";
    case "newTabWhite":
    case "opsWhite":
      return "text-white";
    case "starYellow":
      return "fill-yellow-400 text-yellow-400";
    default:
      return "";
  }
}

const Icon: React.FC<IconProps> = ({
  name,
  className,
  style,
  size: sizeProp,
  width,
  height,
  ...restProps
}) => {
  const Cmp = ICON_MAP[name];
  if (!Cmp) {
    return null;
  }

  const size = resolveSize({
    size: sizeProp,
    width,
    height,
    style,
  });
  const extra = extraClassForName(name);
  const mergedClassName = [extra, className].filter(Boolean).join(" ");

  return (
    <Cmp
      size={size}
      className={mergedClassName || undefined}
      style={style}
      {...restProps}
    />
  );
};

export default Icon;
